import {Component, OnDestroy, OnInit, inject, DestroyRef, ViewEncapsulation} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {NgClass, NgIf} from '@angular/common';
import {Subject, timer, switchMap, exhaustMap, EMPTY} from 'rxjs';
import {filter, takeUntil, catchError} from 'rxjs/operators';

// Services
import {LayoutService} from './shared/services/layout.service';
import {AuthorizationService} from './services/authorization.service';
import {SharedDataService} from './services/shared-data.service';
import {WebSocketManagerService} from './connectivity/services/websocket-manager.service';
import {AgentStateManagerService} from './services/agent-state-manager.service';
import {SessionMonitorService} from './services/session-monitor.service';
import {UserValidationService} from './services/user-validation.service';
import {MessageWebsocketService} from './connectivity/services/message-websocket.service';
import {ConversationWebsocketService} from './connectivity/services/conversation-websocket.service';
import {StateWebsocketService} from './connectivity/services/state-websocket.service';

// Components
import {SidebarComponent} from './components/sidebar/sidebar.component';
import {TopBarComponent} from './components/top-bar/top-bar.component';
import {ConfirmDialog} from 'primeng/confirmdialog';

// Models
import {Agent} from './models/users';
import {ConnectionStatus, NetworkQuality} from './connectivity/models';
import {environment} from './environments/enviroment';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SidebarComponent, TopBarComponent, NgIf, NgClass, ConfirmDialog],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  encapsulation: ViewEncapsulation.None,
  styles: [`
    /* ESTILOS FORZADOS PARA DIÁLOGO DE SESIÓN CERRADA */
    .p-dialog.p-confirm-dialog.session-closed-dialog {
      background-color: #2A2A2A !important;
      border: 2px solid #3A3A3A !important;
      border-radius: 12px !important;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8) !important;
    }

    .p-dialog-mask:has(+ .p-dialog.session-closed-dialog) {
      background-color: rgba(0, 0, 0, 0.85) !important;
      backdrop-filter: blur(10px) !important;
    }

    .p-dialog.session-closed-dialog .p-dialog-header {
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
      color: #FFFFFF !important;
      font-weight: 700 !important;
      padding: 1.5rem 1.75rem !important;
      border-bottom: 3px solid #b45309 !important;
      border-top-left-radius: 10px !important;
      border-top-right-radius: 10px !important;
    }

    .p-dialog.session-closed-dialog .p-dialog-header .p-dialog-title {
      color: #FFFFFF !important;
      font-size: 1.5rem !important;
      font-weight: 700 !important;
    }

    .p-dialog.session-closed-dialog .p-dialog-content {
      background-color: #2A2A2A !important;
      background: #2A2A2A !important;
      color: #FFFFFF !important;
      padding: 2.5rem 2rem !important;
      border-left: 1px solid #3A3A3A !important;
      border-right: 1px solid #3A3A3A !important;
      min-height: 140px !important;
    }

    .p-dialog.session-closed-dialog .p-dialog-content .p-confirm-dialog-message {
      color: #FFFFFF !important;
      font-size: 1.15rem !important;
      line-height: 1.75 !important;
    }

    .p-dialog.session-closed-dialog .p-dialog-content .p-confirm-dialog-icon {
      color: #f59e0b !important;
      font-size: 3.5rem !important;
      animation: pulse-session-warning 2s ease-in-out infinite !important;
    }

    @keyframes pulse-session-warning {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.1); opacity: 0.8; }
    }

    .p-dialog.session-closed-dialog .p-dialog-footer {
      background-color: #0F0F0F !important;
      background: #0F0F0F !important;
      padding: 1.5rem 1.75rem !important;
      border-top: 2px solid #3A3A3A !important;
      border-bottom-left-radius: 10px !important;
      border-bottom-right-radius: 10px !important;
    }

    .p-dialog.session-closed-dialog .p-dialog-footer .p-button.p-button-danger {
      background-color: #dc2626 !important;
      border-color: #dc2626 !important;
      color: #FFFFFF !important;
      padding: 0.875rem 2rem !important;
      font-weight: 600 !important;
    }

    .p-dialog.session-closed-dialog .p-dialog-footer .p-button.p-button-danger:hover {
      background-color: #b91c1c !important;
      transform: translateY(-2px) !important;
    }

    .p-dialog.session-closed-dialog .p-dialog-footer .p-button.p-button-text {
      background-color: transparent !important;
      border: 2px solid #434343 !important;
      color: #E0E0E0 !important;
      padding: 0.875rem 2rem !important;
    }

    .p-dialog.session-closed-dialog .p-dialog-footer .p-button.p-button-text:hover {
      background-color: #434343 !important;
      color: #FFFFFF !important;
    }
  `]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Asesor de Agentes';
  agentId: string | null = null;
  agent: Agent | null = null;

  // Control show sidebar and top bar visibility
  isLogged = false;
  worksel = localStorage.getItem('worksel') ?? '';

  // WebSocket connection state
  isWebSocketConnected = false;
  connectionStatus: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  networkQuality: NetworkQuality = NetworkQuality.GOOD;

  // MEMORY LEAK PREVENTION: Multiple unsubscription strategies
  private readonly destroy$ = new Subject<void>();
  private readonly destroyRef = inject(DestroyRef);

  // MEMORY LEAK FIX: Remove direct interval reference
  private websocket_url: string = environment.wssUrl;
  private readonly base_websocket_url: string = `${environment.wssUrl}UserManagement/`;

  // TOKEN REFRESH CONSTANTS
  private static readonly TOKEN_REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
  private static readonly OPERATIONAL_SESSION_CHECK_INTERVAL_MS = 30 * 1000;
  private static readonly OPERATIONAL_SESSION_REFRESH_BUFFER_MS = environment.production
    ? 5 * 60 * 1000
    : 60 * 1000;

  // AGENT STATE RE-INITIALIZATION TRACKING
  private isReInitializingState = false;
  private lastReInitializationTime: number = 0;
  private lastReInitializationReason: string = '';
  private readonly reInitializationThrottleMs = 2000; // 2 seconds throttle
  private reInitializationAttempts = 0;
  private readonly maxReInitializationAttempts = 3;

  constructor(
    public layoutService: LayoutService,
    private authorizationService: AuthorizationService,
    private sharedDataService: SharedDataService,
    private router: Router,
    private websocketManager: WebSocketManagerService,
    private agentStateManagerService: AgentStateManagerService,
    private sessionMonitor: SessionMonitorService,
    private userValidationService: UserValidationService,
    private messageWebsocketService: MessageWebsocketService,
    private conversationWebsocketService: ConversationWebsocketService,
    private stateWebsocketService: StateWebsocketService
  ) {
  }

  ngOnInit() {
    // Initialize component state with current user data
    this.initializeUserState();

    // Setup router events monitoring
    this.setupRouterEventsMonitoring();

    // Setup user login/logout event listeners
    this.setupUserEventListeners();

    // Initialize token refresh mechanism
    this.initializeTokenRefresh();
    this.initializeAuthenticatedSessionRefresh();
    this.initializeOperationalSessionRefresh();

    // Initialize WebSocket connection monitoring
    this.initializeWebSocketMonitoring();
  }

  ngOnDestroy(): void {
    // Disconnect WebSocket before component destruction
    this.disconnectAllWebSockets();

    // Reset re-initialization state tracking
    this.resetReInitializationState();

    // MEMORY LEAK PREVENTION: Complete the destroy subject
    this.destroy$.next();
    this.destroy$.complete();

    // No need to clear interval manually - using RxJS timer with takeUntil handles it
  }

  get containerClass() {
    return {
      'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
      'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
      'layout-static-inactive': this.layoutService.layoutState().staticMenuDesktopInactive && this.layoutService.layoutConfig().menuMode === 'static',
      'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
      'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive
    };
  }

  /**
   * Get WebSocket connection status for template
   */
  get websocketStatus() {
    return {
      connected: this.isWebSocketConnected,
      status: this.connectionStatus,
      quality: this.networkQuality,
      statusText: this.getConnectionStatusText()
    };
  }

  /**
   * Get human-readable connection status text
   */
  private getConnectionStatusText(): string {
    switch (this.connectionStatus) {
      case ConnectionStatus.CONNECTED:
        return 'Conectado';
      case ConnectionStatus.CONNECTING:
        return 'Conectando...';
      case ConnectionStatus.RECONNECTING:
        return 'Reconectando...';
      case ConnectionStatus.DISCONNECTED:
        return 'Desconectado';
      case ConnectionStatus.FAILED:
        return 'Error de conexión';
      case ConnectionStatus.CIRCUIT_OPEN:
        return 'Conexión suspendida';
      case ConnectionStatus.HIBERNATION_RECOVERY:
        return 'Recuperación de hibernación';
      case ConnectionStatus.WIFI_CHANGE_RECOVERY:
        return 'Recuperación de cambio de WiFi';
      case ConnectionStatus.NETWORK_STABILIZING:
        return 'Estabilizando conexión';
      case ConnectionStatus.APPLICATION_NAVIGATION:
        return 'Navegando por la aplicación';
      default:
        return 'Estado desconocido';
    }
  }

  /**
   * Initialize user state from storage
   */
  private initializeUserState(): void {
    const user = this.sharedDataService.getAgent();
    if (user && this.userValidationService.hasAuthenticatedSession()) {
      console.log('✅ Usuario encontrado en almacenamiento:', user.id);
      this.agent = user;
      this.agentId = user.id;
      if (localStorage.getItem('worksel') === 'complete' || localStorage.getItem('worksel') === 'error') {
        this.isLogged = true
      }

      this.websocket_url = `${this.base_websocket_url}${user.id}?token=${this.authorizationService.getTokenFromStorage()}`;

      // Connect WebSocket for existing logged-in user
      setTimeout(() => {
        this.connectWebSocket();
        this.connectBusinessWebSockets();

        // Iniciar monitoreo de sesión para usuario existente
        this.sessionMonitor.startMonitoring();
      }, 1000); // Small delay to ensure all services are initialized

    } else {
      if (user && !this.userValidationService.hasAuthenticatedSession()) {
        this.sharedDataService.clearUser();
      }
      console.log('ℹ️ No hay usuario en almacenamiento');
      this.agent = null;
      this.agentId = null;
      this.isLogged = false;
    }
  }

  /**
   * Smart re-initialization of agent state with deduplication and throttling
   * This method consolidates all re-initialization triggers and prevents duplicate calls
   */
  private reInitializeAgentState(reason: string = 'unknown'): void {
    const currentTime = Date.now();
    const timeSinceLastCall = currentTime - this.lastReInitializationTime;

    // Validate agent eligibility first
    if (!this.agentId || !this.agent || this.agent.role !== 'agent') {
      console.warn('⚠️ NO SE PUEDE RE-INICIALIZAR ESTADO DEL AGENTE:', {
        timestamp: new Date(currentTime).toISOString(),
        agentId: this.agentId,
        agentRole: this.agent?.role,
        reason: !this.agentId ? 'No hay agentId' :
          !this.agent ? 'No hay datos del agente' :
            this.agent.role !== 'agent' ? 'Usuario no es agente' : 'Razón desconocida'
      });
      return;
    }

    // Check if already in progress
    if (this.isReInitializingState) {
      console.log('🔄 RE-INICIALIZACIÓN YA EN PROGRESO - IGNORANDO LLAMADA DUPLICADA:', {
        timestamp: new Date(currentTime).toISOString(),
        agentId: this.agentId,
        currentReason: reason,
        inProgressReason: this.lastReInitializationReason,
        timeSinceStart: timeSinceLastCall
      });
      return;
    }

    // Throttle rapid calls (except for manual triggers)
    if (timeSinceLastCall < this.reInitializationThrottleMs && reason !== 'manual_trigger') {
      console.log('🚫 RE-INICIALIZACIÓN THROTTLED - LLAMADA MUY RECIENTE:', {
        timestamp: new Date(currentTime).toISOString(),
        agentId: this.agentId,
        reason: reason,
        timeSinceLastCall: timeSinceLastCall,
        throttleThreshold: this.reInitializationThrottleMs
      });
      return;
    }

    // Check maximum attempts within a time window (5 minutes)
    const timeWindow = 5 * 60 * 1000; // 5 minutes
    if (timeSinceLastCall < timeWindow && this.reInitializationAttempts >= this.maxReInitializationAttempts) {
      console.warn('⚠️ MÁXIMO DE INTENTOS DE RE-INICIALIZACIÓN ALCANZADO:', {
        timestamp: new Date(currentTime).toISOString(),
        agentId: this.agentId,
        reason: reason,
        attempts: this.reInitializationAttempts,
        maxAttempts: this.maxReInitializationAttempts,
        timeWindow: timeWindow
      });
      return;
    }

    // Reset attempt counter if enough time has passed
    if (timeSinceLastCall >= timeWindow) {
      this.reInitializationAttempts = 0;
    }

    // Set state tracking
    this.isReInitializingState = true;
    this.lastReInitializationTime = currentTime;
    this.lastReInitializationReason = reason;
    this.reInitializationAttempts++;

    console.log('🔄 INICIANDO RE-INICIALIZACIÓN DE ESTADO DEL AGENTE:', {
      timestamp: new Date(currentTime).toISOString(),
      agentId: this.agentId,
      agentRole: this.agent.role,
      reason: reason,
      attempt: this.reInitializationAttempts,
      isWebSocketConnected: this.isWebSocketConnected,
      connectionStatus: this.connectionStatus
    });

    try {
      this.agentStateManagerService.reInitializeAgentState(this.agentId);

      console.log('✅ RE-INICIALIZACIÓN DE ESTADO COMPLETADA:', {
        timestamp: new Date(currentTime).toISOString(),
        agentId: this.agentId,
        reason: reason,
        attempt: this.reInitializationAttempts,
        success: true
      });
    } catch (error) {
      console.error('❌ ERROR EN RE-INICIALIZACIÓN DE ESTADO:', {
        timestamp: new Date(currentTime).toISOString(),
        agentId: this.agentId,
        reason: reason,
        attempt: this.reInitializationAttempts,
        error: error,
        message: error instanceof Error ? error.message : 'Error desconocido'
      });
    } finally {
      // Reset state tracking after a short delay to allow for final processing
      setTimeout(() => {
        this.isReInitializingState = false;
      }, 500);
    }
  }

  /**
   * Trigger agent state re-initialization with reason tracking
   * This method now routes through the smart reInitializeAgentState method
   */
  private triggerAgentStateReInitialization(reason: string): void {
    console.log('🎯 TRIGGER RE-INICIALIZACIÓN DE ESTADO DEL AGENTE:', {
      timestamp: new Date().toISOString(),
      agentId: this.agentId,
      agentRole: this.agent?.role,
      reason: reason,
      connectionStatus: this.connectionStatus,
      isWebSocketConnected: this.isWebSocketConnected,
      isAlreadyInProgress: this.isReInitializingState
    });

    this.reInitializeAgentState(reason);
  }

  /**
   * Setup router events monitoring with memory leak prevention
   */
  private setupRouterEventsMonitoring(): void {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      // OPTION 1: Using traditional takeUntil pattern
      takeUntil(this.destroy$),
      // OPTION 2: Using Angular v16+ takeUntilDestroyed (more modern approach)
      // takeUntilDestroyed(this.destroyRef),
      catchError(error => {
        console.error('❌ Error in router events:', error);
        return EMPTY;
      })
    ).subscribe({
      next: (event: NavigationEnd) => {
        if (event.urlAfterRedirects.includes('/login') || event.urlAfterRedirects.includes('/workspace-selection')) {
          this.isLogged = false;
        } else {
          const worksel = localStorage.getItem('worksel');
          this.isLogged = (worksel === 'complete' || worksel === 'error') && this.sharedDataService.getAgent() !== null;
        }
      },
      error: (error) => {
        console.error('❌ Error processing router event:', error);
      }
    });
  }

  /**
   * Setup user login/logout event listeners with proper error handling
   */
  private setupUserEventListeners(): void {
    console.log('🔗 Configurando listeners de eventos de usuario');

    // Listen for user login events
    this.sharedDataService.onUserLogin$
      .pipe(
        takeUntil(this.destroy$),
        // Alternative: takeUntilDestroyed(this.destroyRef),
        catchError(error => {
          console.error('❌ Error en listener de login:', error);
          return EMPTY;
        })
      )
      .subscribe({
        next: (user: any) => {
          console.log('🔐 AppComponent: Evento de login recibido para usuario:', user?.id);
          if (user?.id) {
            // Handle user login
            this.handleUserLogin(user);
          }
        }
      });

    // Listen for user logout events
    this.sharedDataService.onUserLogout$
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('❌ Error en listener de logout:', error);
          return EMPTY;
        })
      )
      .subscribe({
        next: () => {
          console.log('🔐 AppComponent: Evento de logout recibido');
          this.handleUserLogout();
        }
      });

    console.log('✅ Listeners de eventos de usuario configurados');
  }

  /**
   * MEMORY LEAK FIX: Use RxJS timer instead of setInterval
   * This approach automatically handles cleanup when the component is destroyed
   */
  private initializeTokenRefresh(): void {
    timer(0, AppComponent.TOKEN_REFRESH_INTERVAL_MS)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          // Only refresh if user is logged in
          if (!this.isLogged) {
            return EMPTY;
          }
          return this.authorizationService.refreshToken();
        }),
        catchError(error => {
          console.error('❌ Error refreshing token:', error);
          // Continue the timer even if one refresh fails
          return EMPTY;
        })
      )
      .subscribe({
        next: () => {
          console.log('🔄 Token actualizado');
          // Refresh WebSocket connection with new token
          this.refreshWebSocketConnection();
        }
      });
  }

  private initializeAuthenticatedSessionRefresh(): void {
    timer(0, 60 * 1000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          if (!this.userValidationService.hasAuthenticatedSession()) {
            return EMPTY;
          }

          if (!this.userValidationService.shouldRefreshAuthenticatedSession()) {
            return EMPTY;
          }

          return this.userValidationService.refreshAuthenticatedSession();
        }),
        catchError(error => {
          console.error('❌ Error refreshing authenticated session:', error);
          return EMPTY;
        })
      )
      .subscribe({
        next: () => {
          console.log('🔄 Sesion autenticada renovada');
        }
      });
  }

  private initializeOperationalSessionRefresh(): void {
    timer(0, AppComponent.OPERATIONAL_SESSION_CHECK_INTERVAL_MS)
      .pipe(
        takeUntil(this.destroy$),
        exhaustMap(() => {
          if (!this.sharedDataService.isUserLoggedIn() || !this.userValidationService.hasAuthenticatedSession()) {
            return EMPTY;
          }

          if (!this.userValidationService.shouldRefreshOperationalSession(
            AppComponent.OPERATIONAL_SESSION_REFRESH_BUFFER_MS
          )) {
            return EMPTY;
          }

          return this.userValidationService.refreshOperationalSession();
        }),
        catchError(error => {
          // TEMPORARY: An expired operational authToken must not log the user out
          // while the backend refresh issue is under investigation. Restore the
          // SessionMonitor handler once the operational token contract is fixed.
          console.warn('AppComponent: Operational session refresh failed; preserving the current session', {
            status: error?.status ?? null,
            statusText: error?.statusText ?? null
          });
          // this.sessionMonitor.handleOperationalSessionRefreshFailure(error);
          return EMPTY;
        })
      )
      .subscribe({
        next: (response) => {
          console.info('AppComponent: Operational session renewed', {
            expiresIn: response.expiresIn,
            deviceSessionId: response.deviceSessionId
          });
        }
      });
  }

  /**
   * Handle user login - extract to method for reusability
   */
  private handleUserLogin(user: Agent): void {
    this.agent = user;
    this.agentId = user.id;

    // Acepta también 'pending' como estado válido de login
    const worksel = localStorage.getItem('worksel');
    if (worksel === 'complete' || worksel === 'error') {
      this.isLogged = true;
    } else {
      this.isLogged = false;
    }
    this.websocket_url = `${this.base_websocket_url}${user.id}?token=${this.authorizationService.getTokenFromStorage()}`;

    console.log('✅ User login handled:', user.id, 'isLogged:', this.isLogged, 'worksel:', worksel);

    // Connect WebSocket after successful login
    this.connectWebSocket();
    this.connectBusinessWebSockets();

    // Iniciar monitoreo de sesión
    this.sessionMonitor.startMonitoring();
  }

  /**
   * Handle user logout - extract to method for reusability
   * This can be triggered by MenuComponent via SharedDataService.clearUser()
   */
  private handleUserLogout(): void {
    console.log('🔐 AppComponent: Procesando evento de logout (posiblemente desde MenuComponent)');

    // Disconnect WebSocket before logout (idempotent - safe if already disconnected)
    this.disconnectAllWebSockets();

    // Detener monitoreo de sesión
    this.sessionMonitor.stopMonitoring();

    this.agent = null;
    this.agentId = null;
    this.isLogged = false;

    console.log('✅ User logout handled in AppComponent');
  }

  /**
   * Initialize WebSocket connection monitoring
   */
  private initializeWebSocketMonitoring(): void {
    console.log('🔗 Configurando monitoreo de WebSocket');

    // Monitor connection state
    this.websocketManager.state$
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('❌ Error en estado de WebSocket:', error);
          return EMPTY;
        })
      )
      .subscribe({
        next: (state) => {
          const previousStatus = this.connectionStatus;
          const previousConnected = this.isWebSocketConnected;

          this.connectionStatus = state.status;
          this.networkQuality = state.quality;
          this.isWebSocketConnected = state.status === ConnectionStatus.CONNECTED;

          // Enhanced logging for connection state changes
          this.logConnectionStateChange(previousStatus, previousConnected, state);
        }
      });

    // Enhanced WebSocket events monitoring with reconnection detection
    this.websocketManager.events$
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('❌ Error en eventos WebSocket:', error);
          return EMPTY;
        })
      )
      .subscribe({
        next: (event) => {
          this.logWebSocketEvent(event);

          // CRITICAL: Detect successful connections and trigger agent state re-initialization
          // Check for 'status' events with data indicating a connection was established
          if (event.type === 'status' && event.data) {
            // Check for successful connection events
            if (event.data.url && (event.data.connectionTime !== undefined || event.data.attempt !== undefined)) {
              this.handleWebSocketConnectionEstablished(event);
            }
          }
        }
      });

    // Monitor heartbeats for connection health
    this.websocketManager.heartbeats$
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('❌ Error en heartbeats - posible pérdida de conexión:', error);
          return EMPTY;
        })
      )
      .subscribe({
        next: (heartbeat) => {
          /*console.log('💓 Heartbeat recibido:', {
            timestamp: heartbeat.timestamp,
            agentId: this.agentId,
            connectionHealthy: true
          });*/
        }
      });

    console.log('✅ Monitoreo de WebSocket configurado');
  }

  /**
   * Handle WebSocket connection established events
   * This includes both initial connections and reconnections
   */
  private handleWebSocketConnectionEstablished(event: any): void {
    const currentTime = new Date().toISOString();

    console.log('🎯 CONEXIÓN WEBSOCKET ESTABLECIDA:', {
      timestamp: currentTime,
      agentId: this.agentId,
      eventData: event.data,
      isReconnection: !!event.data?.wasReconnection || (!!event.data?.attempt && event.data.attempt > 1) || (!!event.data?.totalAttempts && event.data.totalAttempts > 1)
    });

    // Always re-initialize agent state when connection is established for agents
    if (this.agentId && this.agent?.role === 'agent') {
      const isReconnection = !!event.data?.wasReconnection ||
        (!!event.data?.attempt && event.data.attempt > 1) ||
        (!!event.data?.totalAttempts && event.data.totalAttempts > 1);

      const reason = isReconnection ?
        `connection_established_reconnection_attempt_${event.data?.totalAttempts || event.data?.attempt || 'unknown'}` :
        'initial_connection_established';

      console.log('🔄 REINICIALIZANDO ESTADO DEL AGENTE tras conexión establecida:', {
        timestamp: currentTime,
        agentId: this.agentId,
        reason: reason,
        isReconnection: isReconnection,
        eventData: event.data
      });

      // Always re-initialize state when connection is established
      this.triggerAgentStateReInitialization(reason);
    } else {
      console.log('ℹ️ No se reinicializa estado - usuario no es agente:', {
        timestamp: currentTime,
        agentId: this.agentId,
        agentRole: this.agent?.role
      });
    }
  }

  /**
   * Connect WebSocket with current user and token
   */
  private connectWebSocket(): void {
    if (!this.isLogged || !this.websocket_url) {
      console.warn('⚠️ No se puede conectar WebSocket:', {
        timestamp: new Date().toISOString(),
        agentId: this.agentId,
        isLogged: this.isLogged,
        hasUrl: !!this.websocket_url,
        reason: 'Usuario no logueado o URL no disponible'
      });
      return;
    }

    console.log('🔌 INICIANDO CONEXIÓN WEBSOCKET:', {
      timestamp: new Date().toISOString(),
      agentId: this.agentId,
      url: this.websocket_url,
      previousStatus: this.connectionStatus
    });

    this.websocketManager.connect(this.websocket_url)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('❌ ERROR FATAL CONECTANDO WEBSOCKET:', {
            timestamp: new Date().toISOString(),
            agentId: this.agentId,
            error: error,
            url: this.websocket_url
          });
          return EMPTY;
        })
      )
      .subscribe({
        next: (connected) => {
          if (connected) {
            console.log('✅ WEBSOCKET CONECTADO EXITOSAMENTE:', {
              timestamp: new Date().toISOString(),
              agentId: this.agentId,
              url: this.websocket_url,
              connectionStatus: this.connectionStatus
            });
            this.onWebSocketConnected();
          } else {
            console.error('❌ FALLÓ LA CONEXIÓN WEBSOCKET:', {
              timestamp: new Date().toISOString(),
              agentId: this.agentId,
              url: this.websocket_url,
              connectionStatus: this.connectionStatus
            });
          }
        }
      });
  }

  /**
   * Disconnect WebSocket - Safe to call multiple times (idempotent)
   */
  private disconnectWebSocket(): void {
    const currentTime = new Date().toISOString();

    // Check if WebSocket is actually connected before attempting disconnection
    if (this.isWebSocketConnected || this.connectionStatus !== ConnectionStatus.DISCONNECTED) {
      console.log('🔌 DESCONECTANDO WEBSOCKET desde AppComponent:', {
        timestamp: currentTime,
        agentId: this.agentId,
        url: this.websocket_url,
        currentStatus: this.connectionStatus,
        wasConnected: this.isWebSocketConnected,
        initiator: 'AppComponent'
      });

      this.websocketManager.disconnect();
      this.isWebSocketConnected = false;
      this.connectionStatus = ConnectionStatus.DISCONNECTED;

      console.log('✅ WEBSOCKET DESCONECTADO desde AppComponent:', {
        timestamp: currentTime,
        agentId: this.agentId,
        newStatus: this.connectionStatus
      });
    } else {
      console.log('ℹ️ WebSocket ya estaba desconectado (AppComponent):', {
        timestamp: currentTime,
        agentId: this.agentId,
        currentStatus: this.connectionStatus,
        isConnected: this.isWebSocketConnected,
        reason: 'Llamada idempotente - no se requiere acción'
      });
    }
  }

  private connectBusinessWebSockets(): void {
    if (!this.isLogged || !this.agent?.id) {
      return;
    }

    const token = this.authorizationService.getTokenFromStorage();
    if (!token) {
      console.warn('⚠️ No se pueden conectar los sockets de negocio: token no disponible');
      return;
    }

    this.messageWebsocketService.connect(this.agent.id, token);
    this.conversationWebsocketService.connect(this.agent.id, token);
    this.stateWebsocketService.connect(this.agent.id, token);
  }

  private disconnectBusinessWebSockets(): void {
    this.messageWebsocketService.disconnect();
    this.conversationWebsocketService.disconnect();
    this.stateWebsocketService.disconnect();
  }

  private disconnectAllWebSockets(): void {
    this.disconnectBusinessWebSockets();
    this.disconnectWebSocket();
  }

  /**
   * Handle successful WebSocket connection
   */
  private onWebSocketConnected(): void {
    console.log('🎉 WebSocket conectado - configurando funcionalidades');

    // Note: Agent state re-initialization is now handled by handleWebSocketConnectionEstablished
    // The smart deduplication system will prevent duplicate calls
    console.log('ℹ️ Re-inicialización de estado manejada por sistema inteligente de deduplicación');
  }

  /**
   * Public method to manually trigger agent state re-initialization
   * Useful for testing and manual triggers - bypasses throttling
   */
  public manuallyReInitializeAgentState(): void {
    if (!this.isWebSocketConnected) {
      console.warn('⚠️ ESTADO MANUAL: WebSocket no está conectado, no se puede reinicializar estado');
      return;
    }

    console.log('🔄 REINICIALIZACIÓN MANUAL del estado del agente');
    this.reInitializeAgentState('manual_trigger');
  }

  /**
   * Reset re-initialization tracking state
   * Useful for testing or after major state changes
   */
  public resetReInitializationState(): void {
    this.isReInitializingState = false;
    this.lastReInitializationTime = 0;
    this.lastReInitializationReason = '';
    this.reInitializationAttempts = 0;
    console.log('🔄 Estado de tracking de re-inicialización reseteado');
  }

  /**
   * Refresh WebSocket connection with new token
   */
  private refreshWebSocketConnection(): void {
    if (this.isLogged && this.agent) {
      console.log('🔄 REFRESCANDO CONEXIÓN WEBSOCKET CON NUEVO TOKEN:', {
        timestamp: new Date().toISOString(),
        agentId: this.agentId,
        currentlyConnected: this.isWebSocketConnected,
        currentStatus: this.connectionStatus
      });

      // Update WebSocket URL with new token
      const oldUrl = this.websocket_url;
      this.websocket_url = `${this.base_websocket_url}${this.agent.id}?token=${this.authorizationService.getTokenFromStorage()}`;

      console.log('🔑 URL WebSocket actualizada:', {
        timestamp: new Date().toISOString(),
        agentId: this.agentId,
        urlChanged: oldUrl !== this.websocket_url,
        hasNewToken: this.websocket_url.includes('token=')
      });

      this.connectBusinessWebSockets();

      // Reconnect if currently connected
      if (this.isWebSocketConnected) {
        console.log('🔄 Reconectando WebSocket con nuevo token...');
        this.disconnectWebSocket();
        // Small delay to ensure clean disconnection
        setTimeout(() => {
          this.connectWebSocket();
        }, 1000);
      } else {
        console.log('ℹ️ WebSocket estaba conectado, no se requiere reconexión');
      }
    } else {
      console.warn('⚠️ No se puede refrescar conexión WebSocket:', {
        timestamp: new Date().toISOString(),
        isLogged: this.isLogged,
        hasAgent: !!this.agent,
        agentId: this.agentId
      });
    }
  }

  /**
   * Handle WebSocket reconnection - re-initialize agent state when connection is restored
   */
  private handleWebSocketReconnection(wasReconnection: boolean): void {
    const currentTime = new Date().toISOString();

    if (wasReconnection) {
      console.log('🔄 MANEJANDO RECONEXIÓN WEBSOCKET:', {
        timestamp: currentTime,
        agentId: this.agentId,
        agentRole: this.agent?.role,
        willReinitializeState: this.agentId && this.agent?.role === 'agent'
      });

      // Re-initialize agent state only for agents (not supervisors) after reconnection
      if (this.agentId && this.agent?.role === 'agent') {
        console.log('🔄 RE-INICIALIZANDO ESTADO DEL AGENTE después de reconexión:', {
          timestamp: currentTime,
          agentId: this.agentId,
          reason: 'WebSocket reconnection detected'
        });

        // Re-initialize agent state after reconnection
        this.reInitializeAgentState('websocket_reconnection_detected');
      } else {
        console.log('ℹ️ NO SE RE-INICIALIZA ESTADO:', {
          timestamp: currentTime,
          agentId: this.agentId,
          agentRole: this.agent?.role,
          reason: this.agentId ? 'Usuario no es agente' : 'No hay agentId disponible'
        });
      }
    } else {
      console.log('ℹ️ Conexión inicial establecida (no es reconexión):', {
        timestamp: currentTime,
        agentId: this.agentId,
        agentRole: this.agent?.role
      });
    }

    // Send initial ping after any successful connection
    this.websocketManager.sendPing();
  }

  /**
   * Enhanced logging for connection state changes
   */
  private logConnectionStateChange(previousStatus: ConnectionStatus, previousConnected: boolean, currentState: any): void {
    const currentTime = new Date().toISOString();
    const statusChanged = previousStatus !== currentState.status;
    const connectionChanged = previousConnected !== this.isWebSocketConnected;

    // Always log state if there's a change
    if (statusChanged || connectionChanged) {
      console.log('📡 WebSocket estado cambió:', {
        timestamp: currentTime,
        agentId: this.agentId,
        previous: {
          status: previousStatus,
          connected: previousConnected
        },
        current: {
          status: currentState.status,
          connected: this.isWebSocketConnected,
          quality: currentState.quality,
          reconnectAttempts: currentState.reconnectAttempts
        }
      });
    }

    // Special logging for connection loss
    if (previousConnected && !this.isWebSocketConnected) {
      console.warn('🔥 CONEXIÓN WEBSOCKET PERDIDA:', {
        timestamp: currentTime,
        agentId: this.agentId,
        previousStatus: previousStatus,
        currentStatus: currentState.status,
        networkQuality: currentState.quality,
        reconnectAttempts: currentState.reconnectAttempts,
        url: this.websocket_url
      });
    }

    // Special logging for connection recovery AND re-initialize agent state
    if (!previousConnected && this.isWebSocketConnected) {
      const wasReconnection = currentState.reconnectAttempts > 0;
      console.log('✅ CONEXIÓN WEBSOCKET RECUPERADA:', {
        timestamp: currentTime,
        agentId: this.agentId,
        previousStatus: previousStatus,
        currentStatus: currentState.status,
        networkQuality: currentState.quality,
        reconnectAttempts: currentState.reconnectAttempts,
        url: this.websocket_url,
        wasReconnection: wasReconnection
      });

      // CRITICAL: Re-initialize agent state after successful connection/reconnection
      if (this.agentId && this.agent?.role === 'agent') {
        const reason = wasReconnection ?
          `state_change_reconnection_attempt_${currentState.reconnectAttempts}` :
          'state_change_initial_connection';

        console.log('🔄 REINICIALIZANDO ESTADO DEL AGENTE tras cambio de estado:', {
          timestamp: currentTime,
          agentId: this.agentId,
          reason: reason,
          wasReconnection: wasReconnection,
          reconnectAttempts: currentState.reconnectAttempts
        });

        this.triggerAgentStateReInitialization(reason);
      }

      // Also call the original reconnection handler for backwards compatibility
      this.handleWebSocketReconnection(wasReconnection);
    }

    // Special logging for reconnection attempts
    if (currentState.status === ConnectionStatus.RECONNECTING) {
      console.log('🔄 INTENTANDO RECONEXIÓN WEBSOCKET:', {
        timestamp: currentTime,
        agentId: this.agentId,
        attempt: currentState.reconnectAttempts,
        networkQuality: currentState.quality,
        url: this.websocket_url
      });
    }

    // Special logging for circuit breaker
    if (currentState.status === ConnectionStatus.CIRCUIT_OPEN) {
      console.error('⚡ CIRCUIT BREAKER ACTIVADO - CONEXIÓN SUSPENDIDA:', {
        timestamp: currentTime,
        agentId: this.agentId,
        failedAttempts: currentState.reconnectAttempts,
        networkQuality: currentState.quality,
        url: this.websocket_url
      });
    }

    // Special logging for connection failures
    if (currentState.status === ConnectionStatus.FAILED) {
      console.error('❌ FALLO EN CONEXIÓN WEBSOCKET:', {
        timestamp: currentTime,
        agentId: this.agentId,
        networkQuality: currentState.quality,
        reconnectAttempts: currentState.reconnectAttempts,
        url: this.websocket_url
      });
    }
  }

  /**
   * Log WebSocket events (connections, disconnections, errors)
   */
  private logWebSocketEvent(event: any): void {
    const currentTime = new Date().toISOString();

    switch (event.type) {
      case 'status':
        if (event.data?.code || event.data?.reason) {
          console.log('📊 WebSocket Status Event:', {
            timestamp: currentTime,
            agentId: this.agentId,
            type: event.type,
            data: event.data,
            url: this.websocket_url
          });
        }
        break;

      case 'error':
        console.error('🚨 WEBSOCKET ERROR EVENT:', {
          timestamp: currentTime,
          agentId: this.agentId,
          type: event.type,
          error: event.data?.error,
          timeout: event.data?.timeout,
          url: event.data?.url || this.websocket_url
        });
        break;

      case 'message':
        // Only log sent messages, not received (those are handled elsewhere)
        if (event.data?.data) {
          console.log('📤 WebSocket Message Sent:', {
            timestamp: currentTime,
            agentId: this.agentId,
            messageType: event.data.data.event_type,
            size: JSON.stringify(event.data.data).length
          });
        }
        break;

      default:
        console.log('📡 WebSocket Event:', {
          timestamp: currentTime,
          agentId: this.agentId,
          type: event.type,
          data: event.data
        });
    }
  }

  protected readonly sessionStorage = sessionStorage;
}
